import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Search, 
  CheckCircle2, 
  Clock, 
  AlertTriangle, 
  Calendar,
  TrendingUp,
  BarChart3,
  Activity,
  FileText,
  ChevronDown,
  ChevronRight,
  Target,
  DollarSign
} from 'lucide-react';
import { useVentureDeals } from '@/hooks/useVentureDeals';
import { GanttChart } from '@/components/deals/GanttChart';
import { LoadingFallback } from '@/components/LoadingFallback';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface TimelineItem {
  id: string;
  title: string;
  description?: string;
  type: 'task';
  status: string;
  startDate?: string;
  dueDate?: string;
  assignedTo?: string;
  dealId?: string;
  dealTitle?: string;
  priority?: string;
  progress?: number;
  amount?: number;
  payment_status?: string;
}

export const VentureTimelineTasksOverview = () => {
  const { deals, loading, error, updateTask } = useVentureDeals();
  
  const [activeView, setActiveView] = useState('timeline');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedDeal, setSelectedDeal] = useState('all');
  const [expandedDeals, setExpandedDeals] = useState<Set<string>>(new Set());

  // Helper to toggle deal expansion
  const toggleDeal = (dealId: string) => {
    setExpandedDeals(prev => {
      const next = new Set(prev);
      if (next.has(dealId)) {
        next.delete(dealId);
      } else {
        next.add(dealId);
      }
      return next;
    });
  };

  // Helper to expand all deals
  const expandAll = () => {
    const allDealIds = new Set(filteredItems.map(item => item.dealId).filter(Boolean) as string[]);
    setExpandedDeals(allDealIds);
  };

  // Helper to collapse all deals
  const collapseAll = () => {
    setExpandedDeals(new Set());
  };

  // Aggregate all timeline items from venture deals
  const timelineItems = useMemo(() => {
    const items: TimelineItem[] = [];
    
    deals.forEach(deal => {
      const taskList = (deal as any).tasks || (deal as any).deal_tasks || [];
      
      if (taskList && taskList.length > 0) {
        taskList.forEach((task: any) => {
          items.push({
            id: task.id,
            title: task.title,
            description: task.description,
            type: 'task',
            status: task.status || 'pending',
            startDate: task.start_date,
            dueDate: task.due_date,
            assignedTo: task.assigned_to,
            dealId: deal.id,
            dealTitle: deal.title || 'Untitled Investment',
            priority: 'medium',
            progress: task.progress_percentage,
            amount: task.payment_amount,
            payment_status: task.payment_status
          });
        });
      }
    });
    
    return items.sort((a, b) => {
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
    });
  }, [deals]);

  // Filter timeline items
  const filteredItems = useMemo(() => {
    return timelineItems.filter(item => {
      const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (item.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (item.dealTitle || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
      const matchesDeal = selectedDeal === 'all' || item.dealId === selectedDeal;
      
      return matchesSearch && matchesStatus && matchesDeal;
    });
  }, [timelineItems, searchTerm, statusFilter, selectedDeal]);

  // Calculate timeline metrics
  const timelineMetrics = useMemo(() => {
    const total = timelineItems.length;
    const completed = timelineItems.filter(item => item.status === 'completed').length;
    const pending = timelineItems.filter(item => item.status === 'pending').length;
    const overdue = timelineItems.filter(item => {
      if (!item.dueDate) return false;
      const dueDate = new Date(item.dueDate);
      const today = new Date();
      return dueDate < today && item.status !== 'completed';
    }).length;

    const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;

    return {
      total,
      completed,
      pending,
      overdue,
      completionRate
    };
  }, [timelineItems]);

  // Chart data for timeline visualization
  const chartData = useMemo(() => {
    const monthlyData: { [key: string]: any } = {};
    
    timelineItems.forEach(item => {
      if (!item.dueDate) return;
      
      const date = new Date(item.dueDate);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          month: monthKey,
          tasks: 0,
          completed: 0,
          overdue: 0
        };
      }
      
      monthlyData[monthKey].tasks++;
      
      if (item.status === 'completed') {
        monthlyData[monthKey].completed++;
      } else if (new Date(item.dueDate) < new Date()) {
        monthlyData[monthKey].overdue++;
      }
    });
    
    return Object.values(monthlyData).slice(-6); // Last 6 months
  }, [timelineItems]);

  // Get unique deal options for filters
  const dealOptions = useMemo(() => deals.map(d => ({ id: d.id, title: d.title || 'Untitled Investment' })), [deals]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (status: string) => {
    if (status === 'completed') {
      return (
        <Badge className="bg-success/10 text-success border-success/20 font-medium">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Completed
        </Badge>
      );
    } else if (status === 'pending') {
      return (
        <Badge className="bg-warning/10 text-warning border-warning/20 font-medium">
          <Clock className="h-3 w-3 mr-1" />
          Pending
        </Badge>
      );
    } else if (status === 'in_progress' || status === 'processing') {
      return (
        <Badge className="bg-primary/10 text-primary border-primary/20 font-medium">
          <Activity className="h-3 w-3 mr-1" />
          In Progress
        </Badge>
      );
    }

    return (
      <Badge variant="outline" className="font-medium capitalize">
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  const handleMarkComplete = async (taskId: string) => {
    try {
      await updateTask(taskId, { status: 'completed', progress_percentage: 100 });
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  if (loading) return <LoadingFallback />;
  if (error) return <div className="text-destructive p-4">Error: {error}</div>;

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Total Tasks</CardDescription>
            <CardTitle className="text-3xl">{timelineMetrics.total}</CardTitle>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Completed</CardDescription>
            <CardTitle className="text-3xl text-success">{timelineMetrics.completed}</CardTitle>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Pending</CardDescription>
            <CardTitle className="text-3xl text-warning">{timelineMetrics.pending}</CardTitle>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Overdue</CardDescription>
            <CardTitle className="text-3xl text-destructive">{timelineMetrics.overdue}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs value={activeView} onValueChange={setActiveView}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="timeline">
            <Activity className="h-4 w-4 mr-2" />
            Timeline
          </TabsTrigger>
          <TabsTrigger value="chart">
            <BarChart3 className="h-4 w-4 mr-2" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="gantt">
            <Calendar className="h-4 w-4 mr-2" />
            Gantt
          </TabsTrigger>
        </TabsList>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mt-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>

          <Select value={selectedDeal} onValueChange={setSelectedDeal}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="All Investments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Investments</SelectItem>
              {dealOptions.map(deal => (
                <SelectItem key={deal.id} value={deal.id}>{deal.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <TabsContent value="timeline" className="space-y-4 mt-4">
          <Card className="border-0 shadow-sm">
            <CardHeader className="border-b bg-muted/30">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-xl">Timeline by Investment</CardTitle>
                  <CardDescription>
                    Organized by investment project {filteredItems.length !== timelineItems.length && `(${filteredItems.length} of ${timelineItems.length})`}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={expandAll}
                    className="text-xs"
                  >
                    Expand All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={collapseAll}
                    className="text-xs"
                  >
                    Collapse All
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {filteredItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Activity className="h-12 w-12 text-muted-foreground/30 mb-3" />
                  <p className="text-muted-foreground font-medium">No timeline items match your filters</p>
                  <p className="text-sm text-muted-foreground/70 mt-1">Try adjusting your search or filters</p>
                </div>
              ) : (
                (() => {
                  // Group items by deal
                  const itemsByDeal = new Map<string, {
                    dealId: string;
                    dealTitle: string;
                    items: TimelineItem[];
                  }>();

                  filteredItems.forEach(item => {
                    const dealId = item.dealId || 'no-deal';
                    const dealTitle = item.dealTitle || 'Unassigned Items';
                    
                    if (!itemsByDeal.has(dealId)) {
                      itemsByDeal.set(dealId, {
                        dealId,
                        dealTitle,
                        items: []
                      });
                    }
                    
                    itemsByDeal.get(dealId)!.items.push(item);
                  });

                  // Sort deals by title
                  const sortedDeals = Array.from(itemsByDeal.values()).sort((a, b) => 
                    a.dealTitle.localeCompare(b.dealTitle)
                  );

                  // Calculate metrics for each deal
                  const dealMetrics = new Map<string, {
                    total: number;
                    completed: number;
                    overdue: number;
                    pending: number;
                    completionRate: number;
                  }>();

                  sortedDeals.forEach(deal => {
                    const total = deal.items.length;
                    const completed = deal.items.filter(item => item.status === 'completed').length;
                    
                    const overdue = deal.items.filter(item => {
                      if (!item.dueDate) return false;
                      const dueDate = new Date(item.dueDate);
                      const today = new Date();
                      return dueDate < today && item.status !== 'completed';
                    }).length;

                    const pending = deal.items.filter(item => item.status === 'pending').length;
                    const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;

                    dealMetrics.set(deal.dealId, {
                      total,
                      completed,
                      overdue,
                      pending,
                      completionRate
                    });
                  });

                  return (
                    <div className="space-y-4">
                      {sortedDeals.map((deal) => {
                        const metrics = dealMetrics.get(deal.dealId)!;
                        const isExpanded = expandedDeals.has(deal.dealId);
                        
                        return (
                          <Collapsible
                            key={deal.dealId}
                            open={isExpanded}
                            onOpenChange={() => toggleDeal(deal.dealId)}
                          >
                            {/* Deal Header */}
                            <div className={`border rounded-lg transition-all duration-200 ${
                              isExpanded ? 'border-primary/40 shadow-md' : 'border-border hover:border-primary/20 hover:shadow-sm'
                            }`}>
                              <CollapsibleTrigger asChild>
                                <div className="w-full p-4 cursor-pointer group">
                                  <div className="flex items-start justify-between gap-4">
                                    {/* Left: Deal Info */}
                                    <div className="flex items-start gap-3 flex-1 min-w-0">
                                      <div className={`flex items-center justify-center shrink-0 transition-transform duration-200`}>
                                        {isExpanded ? (
                                          <ChevronDown className="h-5 w-5 text-primary" />
                                        ) : (
                                          <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary" />
                                        )}
                                      </div>
                                      
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                          <h3 className="font-semibold text-lg text-foreground truncate">
                                            {deal.dealTitle}
                                          </h3>
                                        </div>
                                        
                                        {/* Deal Metrics */}
                                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                                          <span className="flex items-center gap-1">
                                            <Activity className="h-3 w-3" />
                                            {metrics.total} items
                                          </span>
                                          <span className="flex items-center gap-1 text-success">
                                            <CheckCircle2 className="h-3 w-3" />
                                            {metrics.completed} completed
                                          </span>
                                          {metrics.pending > 0 && (
                                            <span className="flex items-center gap-1 text-warning">
                                              <Clock className="h-3 w-3" />
                                              {metrics.pending} pending
                                            </span>
                                          )}
                                          {metrics.overdue > 0 && (
                                            <span className="flex items-center gap-1 text-destructive font-medium">
                                              <AlertTriangle className="h-3 w-3" />
                                              {metrics.overdue} overdue
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Right: Completion Rate */}
                                    <div className="flex items-center gap-3 shrink-0">
                                      <div className="text-right">
                                        <div className="text-lg font-bold text-foreground">
                                          {metrics.completionRate}%
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                          complete
                                        </div>
                                      </div>
                                      <div className="w-16 h-16">
                                        <svg className="transform -rotate-90" viewBox="0 0 100 100">
                                          <circle
                                            cx="50"
                                            cy="50"
                                            r="40"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="8"
                                            className="text-muted opacity-20"
                                          />
                                          <circle
                                            cx="50"
                                            cy="50"
                                            r="40"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="8"
                                            strokeDasharray={`${metrics.completionRate * 2.51} 251`}
                                            className={`transition-all duration-500 ${
                                              metrics.completionRate === 100 ? 'text-success' :
                                              metrics.completionRate >= 50 ? 'text-primary' :
                                              'text-warning'
                                            }`}
                                          />
                                        </svg>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </CollapsibleTrigger>
                              
                              {/* Deal Items (when expanded) */}
                              <CollapsibleContent>
                                <div className="border-t p-4 bg-muted/20">
                                  <div className="space-y-3">
                                    {deal.items
                                      .sort((a, b) => {
                                        if (!a.dueDate) return 1;
                                        if (!b.dueDate) return -1;
                                        return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
                                      })
                                      .map((item) => {
                                        const isOverdue = item.dueDate && new Date(item.dueDate) < new Date() && item.status !== 'completed';
                                        
                                        return (
                                          <div key={item.id} className="animate-fade-in">
                                            <div className={`relative bg-card border rounded-lg p-4 transition-all duration-200 hover:shadow-lg hover:border-primary/20 hover:-translate-y-0.5 ${
                                              isOverdue ? 'border-l-4 border-l-destructive' : ''
                                            }`}>
                                              {/* Header */}
                                              <div className="flex items-start justify-between gap-3 mb-2">
                                                <div className="flex items-start gap-3 flex-1 min-w-0">
                                                  <div className="flex items-center justify-center w-9 h-9 rounded-lg shrink-0 bg-primary/10 text-primary">
                                                    <FileText className="h-4 w-4" />
                                                  </div>
                                                  <div className="flex-1 min-w-0">
                                                    <h5 className="font-semibold text-base text-foreground mb-1 truncate">{item.title}</h5>
                                                    {item.description && (
                                                      <p className="text-sm text-muted-foreground line-clamp-2">{item.description}</p>
                                                    )}
                                                  </div>
                                                </div>
                                                
                                                {/* Status Badge */}
                                                <div className="shrink-0">
                                                  {getStatusBadge(item.status)}
                                                </div>
                                              </div>

                                              {/* Progress Bar */}
                                              {typeof item.progress === 'number' && (
                                                <div className="mb-3">
                                                  <div className="flex items-center justify-between text-xs mb-1">
                                                    <span className="text-muted-foreground">Progress</span>
                                                    <span className="font-medium text-foreground">{item.progress}%</span>
                                                  </div>
                                                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                                                    <div 
                                                      className="h-full bg-primary transition-all duration-300 rounded-full"
                                                      style={{ width: `${item.progress}%` }}
                                                    />
                                                  </div>
                                                </div>
                                              )}

                                              {/* Metadata */}
                                              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground mb-3">
                                                <Badge variant="secondary" className="text-xs font-normal capitalize">
                                                  {item.type}
                                                </Badge>
                                                {item.startDate && (
                                                  <span className="flex items-center gap-1">
                                                    <Calendar className="h-3 w-3" />
                                                    Start: {formatDate(item.startDate)}
                                                  </span>
                                                )}
                                                {item.dueDate && (
                                                  <span className={`flex items-center gap-1 font-medium ${isOverdue ? 'text-destructive' : ''}`}>
                                                    <Calendar className="h-3 w-3" />
                                                    Due: {formatDate(item.dueDate)}
                                                  </span>
                                                )}
                                              </div>

                                              {/* Payment Status Badges */}
                                              {item.amount && item.amount > 0 && (
                                                <div className="flex items-center gap-2 mt-2">
                                                  {item.payment_status === 'paid' && (
                                                    <Badge className="bg-green-600 text-white">
                                                      <CheckCircle2 className="h-3 w-3 mr-1" />
                                                      Paid
                                                    </Badge>
                                                  )}
                                                  {item.payment_status === 'due' && (
                                                    <Badge className="bg-orange-600 text-white">
                                                      <Clock className="h-3 w-3 mr-1" />
                                                      Payment Due
                                                    </Badge>
                                                  )}
                                                  {item.payment_status === 'unpaid' && item.status === 'completed' && (
                                                    <Badge className="bg-yellow-600 text-white">
                                                      <AlertTriangle className="h-3 w-3 mr-1" />
                                                      Unpaid
                                                    </Badge>
                                                  )}
                                                  {(!item.payment_status || item.payment_status === 'unpaid') && item.status !== 'completed' && (
                                                    <Badge variant="outline" className="border-muted-foreground/30">
                                                      <DollarSign className="h-3 w-3 mr-1" />
                                                      ${item.amount.toLocaleString()}
                                                    </Badge>
                                                  )}
                                                </div>
                                              )}

                                              {/* Action Buttons */}
                                              <div className="mt-3 flex items-center gap-2">
                                                {item.status !== 'completed' && (
                                                  <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleMarkComplete(item.id)}
                                                  >
                                                    <CheckCircle2 className="h-4 w-4 mr-1" />
                                                    Complete
                                                  </Button>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })}
                                  </div>
                                </div>
                              </CollapsibleContent>
                            </div>
                          </Collapsible>
                        );
                      })}
                    </div>
                  );
                })()
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="chart" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Task Timeline Overview</CardTitle>
              <CardDescription>Monthly breakdown of investment tasks</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="tasks" fill="hsl(var(--primary))" name="Tasks" />
                  <Bar dataKey="completed" fill="hsl(var(--success))" name="Completed" />
                  <Bar dataKey="overdue" fill="hsl(var(--destructive))" name="Overdue" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="gantt" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Investment Timeline (Gantt View)</CardTitle>
              <CardDescription>Visual timeline of all investment tasks</CardDescription>
            </CardHeader>
            <CardContent>
              <GanttChart tasks={timelineItems.map(item => ({
                id: item.id,
                title: item.title,
                description: item.description || '',
                status: item.status,
                due_date: item.dueDate || '',
                assigned_to: item.assignedTo || null,
                progress_percentage: item.progress || 0,
                deal_id: item.dealId || '',
                completion_date: item.status === 'completed' ? item.dueDate || null : null,
                depends_on_task_id: null,
                created_at: '',
                updated_at: ''
              }))} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
